import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus } from '@phosphor-icons/react';
import { toast } from 'sonner';

export const PharmacyReturnsPage = () => {
  const [returns, setReturns] = useState([]);
  const [sales, setSales] = useState([]);
  const [packages, setPackages] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [reason, setReason] = useState('');

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const now = new Date();
      const [retRes, salesRes, pkgRes] = await Promise.all([
        api.get('/returns'),
        api.get(`/sales?month=${now.getMonth() + 1}&year=${now.getFullYear()}`),
        api.get('/packages'),
      ]);
      setReturns(retRes.data);
      setSales(salesRes.data);
      setPackages(pkgRes.data);
    } catch (error) { console.error(error); }
  };

  const openModalFor = (sale) => {
    setSelectedSale(sale);
    setQuantity(1);
    setReason('');
    setShowModal(true);
  };

  const submit = async () => {
    if (!selectedSale) return;
    if (!reason.trim()) return toast.error('Reason is required');
    const maxReturnable = selectedSale._remaining ?? selectedSale.quantity;
    if (quantity < 1 || quantity > maxReturnable) return toast.error(`Quantity must be 1-${maxReturnable}`);
    try {
      await api.post('/returns', {
        sale_id: selectedSale.id,
        quantity: parseInt(quantity),
        reason,
      });
      toast.success('Return recorded');
      setShowModal(false);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    }
  };

  const getPkg = (id) => packages.find(p => p.id === id);

  // A5: cumulative returned qty per sale; hide fully-returned, show remaining on partials
  const returnedBySale = returns.reduce((m, r) => { m[r.sale_id] = (m[r.sale_id] || 0) + (r.quantity || 0); return m; }, {});
  const remainingFor = (sale) => Math.max(0, (sale.quantity || 0) - (returnedBySale[sale.id] || 0));

  return (
    <div className="min-h-screen p-4" style={{ backgroundColor: '#F8FAFC', fontFamily: 'IBM Plex Sans' }}>
      <h1 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Manrope', color: '#1E3A8A' }}>Customer Returns</h1>

      <Card className="p-4 mb-6">
        <h2 className="font-bold mb-3" style={{ fontFamily: 'Manrope' }}>Recent Sales — Tap to Return</h2>
        <div className="space-y-2">
          {sales.map(s => {
            const remaining = remainingFor(s);
            const fullyReturned = remaining === 0;
            return (
              <div
                key={s.id}
                data-testid={`sale-return-${s.id}`}
                onClick={() => !fullyReturned && openModalFor({ ...s, _remaining: remaining })}
                className={`flex justify-between items-center p-3 border border-slate-200 rounded-lg ${fullyReturned ? 'opacity-60 bg-slate-50' : 'cursor-pointer hover:bg-slate-50'}`}
              >
                <div>
                  <p className="font-semibold text-sm">{getPkg(s.package_id)?.name || 'Unknown'}</p>
                  <p className="text-xs text-slate-500">{new Date(s.sale_datetime).toLocaleDateString()} · Qty {s.quantity} · RM {s.total_amount?.toFixed(2)}</p>
                  {!fullyReturned && remaining < s.quantity && (
                    <p className="text-xs text-orange-600 font-semibold mt-1">Remaining returnable: {remaining} of {s.quantity}</p>
                  )}
                </div>
                {fullyReturned ? (
                  <Badge className="bg-slate-200 text-slate-700 border-slate-300">Returned</Badge>
                ) : (
                  <Button size="sm" variant="outline"><Plus size={14} className="mr-1" /> Return</Button>
                )}
              </div>
            );
          })}
          {sales.length === 0 && <p className="text-sm text-slate-500 text-center py-4">No sales this month</p>}
        </div>
      </Card>

      <h2 className="font-bold mb-3" style={{ fontFamily: 'Manrope' }}>Recent Returns</h2>
      <div className="space-y-3">
        {returns.map(r => (
          <Card key={r.id} data-testid={`return-${r.id}`} className="p-4">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-semibold">Qty {r.quantity} returned</p>
                <p className="text-xs text-slate-600 mt-1">{r.reason}</p>
                <p className="text-xs text-slate-400 mt-1">{new Date(r.created_at).toLocaleString()}</p>
              </div>
              <div className="text-right">
                <Badge className="bg-blue-50 text-blue-700 border-blue-200">{r.handling.replace('_', ' ')}</Badge>
                <p className="text-xs text-slate-600 mt-1">-RM {r.total_reversed?.toFixed(2)}</p>
              </div>
            </div>
          </Card>
        ))}
        {returns.length === 0 && <p className="text-sm text-slate-500 text-center py-4">No returns yet</p>}
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record Customer Return</DialogTitle></DialogHeader>
          {selectedSale && (
            <div className="space-y-4">
              <div className="text-sm bg-slate-50 p-3 rounded-lg">
                <p className="font-semibold">{getPkg(selectedSale.package_id)?.name}</p>
                <p className="text-xs text-slate-600">Original qty: {selectedSale.quantity} · Total: RM {selectedSale.total_amount?.toFixed(2)}</p>
              </div>
              <div>
                <Label>Return Quantity (max {selectedSale._remaining ?? selectedSale.quantity})</Label>
                <Input data-testid="return-qty" type="number" min="1" max={selectedSale._remaining ?? selectedSale.quantity} value={quantity} onChange={(e) => setQuantity(parseInt(e.target.value))} />
              </div>
              <div>
                <Label>Reason (required)</Label>
                <Textarea data-testid="return-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Customer complained about taste" rows={3} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button data-testid="submit-return-btn" onClick={submit} style={{ backgroundColor: '#1E3A8A' }}>Record Return</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
